import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { CRMTaskForm } from './CRMTaskForm';
import { Button, ConfirmDeleteModal, SearchInput } from '@/components';
import { V2Table } from '@/common/components';
import { TableChip } from '../../components/PremiumDesign';
import editIcon from '../../assets/editIcon.png';
import trashIcon from '../../assets/trashIcon.png';
import {
  getCRMContactTasks,
  createCRMContactTask,
  updateCRMContactTask,
  deleteCRMContactTask,
  completeCRMContactTask,
} from '../api';
import { parseApiError } from '@/common/utils';
import { pageWindow } from '../utils/pagedList';

const PAGE_SIZE = 10;

const STAT_FILTERS = [
  { id: 'all', label: 'Total', dot: '#3B82F6' },
  { id: 'pending', label: 'Pending', dot: '#F97316' },
  { id: 'completed', label: 'Completed', dot: '#50AB3D' },
];

function getCurrentUserId() {
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      return user?.employee_id || user?.id;
    }
  } catch {
    // ignore
  }
  return null;
}

function getAssigneeName(task) {
  return (
    task.assigned_to_employee_name || task.assigned_to_name || 'Unassigned'
  );
}

function formatDate(dateString) {
  if (!dateString) return '-';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
}

export function CRMTasksSection({
  companyId,
  contactId,
  readOnly = false,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}) {
  const queryClient = useQueryClient();
  const [editingTask, setEditingTask] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statFilter, setStatFilter] = useState('all');
  const [page, setPage] = useState(1);
  const taskFormRef = useRef(null);
  const currentUserId = getCurrentUserId();
  const isPreviewing = !!editingTask && !isEditMode;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data: tasksData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      'crm-contact-tasks',
      companyId,
      contactId,
      statFilter,
      debouncedSearch,
      page,
    ],
    queryFn: () =>
      getCRMContactTasks({
        companyId,
        contactId,
        params: {
          status: statFilter,
          page,
          page_size: PAGE_SIZE,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      }),
    enabled: !!companyId && !!contactId,
  });

  const { rows: pagedTasks, totalCount } = pageWindow(
    tasksData,
    page,
    PAGE_SIZE,
  );

  // Unfiltered fetch (no status/search) purely to compute accurate chip counts.
  const { data: statsData } = useQuery({
    queryKey: ['crm-contact-tasks-stats', companyId, contactId],
    queryFn: () =>
      getCRMContactTasks({
        companyId,
        contactId,
        params: { status: 'all', page_size: 1000 },
      }),
    enabled: !!companyId && !!contactId,
  });

  const stats = useMemo(() => {
    const statsTasks = statsData?.results || [];
    return {
      all: statsTasks.length,
      mine: statsTasks.filter(
        (t) =>
          currentUserId &&
          String(t.assigned_to_employee_id || t.assigned_to_id) ===
            String(currentUserId),
      ).length,
      pending: statsTasks.filter((t) => !t.completed_at).length,
      completed: statsTasks.filter((t) => !!t.completed_at).length,
    };
  }, [statsData, currentUserId]);

  const invalidateTasks = () => {
    queryClient.invalidateQueries({
      queryKey: ['crm-contact-tasks', companyId, contactId],
    });
    queryClient.invalidateQueries({
      queryKey: ['crm-contact-tasks-stats', companyId, contactId],
    });
  };

  const { mutate: handleAddTask, isPending: isAdding } = useMutation({
    mutationFn: (vars) => {
      if (!canCreate) throw new Error('Permission denied.');
      return createCRMContactTask(vars);
    },
    onSuccess: () => {
      toast.success('Task added successfully!');
      setPage(1);
      invalidateTasks();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to add task.');
    },
  });

  const { mutate: handleUpdateTask, isPending: isUpdating } = useMutation({
    mutationFn: (vars) => {
      if (!canUpdate) throw new Error('Permission denied.');
      return updateCRMContactTask(vars);
    },
    onSuccess: () => {
      toast.success('Task updated successfully!');
      setEditingTask(null);
      setIsEditMode(false);
      invalidateTasks();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update task.');
    },
  });

  const { mutate: handleDeleteTask } = useMutation({
    mutationFn: (vars) => {
      if (!canDelete) throw new Error('Permission denied.');
      return deleteCRMContactTask(vars);
    },
    onSuccess: () => {
      toast.success('Task deleted successfully!');
      if (taskToDelete === editingTask?.id) {
        setEditingTask(null);
        setIsEditMode(false);
      }
      setTaskToDelete(null);
      invalidateTasks();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete task.');
    },
  });

  const { mutate: handleCompleteTask, isPending: isCompleting } = useMutation({
    mutationFn: (vars) => {
      if (!canUpdate) throw new Error('Permission denied.');
      return completeCRMContactTask(vars);
    },
    onSuccess: () => {
      toast.success('Task status updated successfully!');
      invalidateTasks();
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update task status.');
    },
  });

  const submitAddTask = (payload) => {
    handleAddTask({ companyId, contactId, payload });
  };

  const submitEditTask = (payload) => {
    handleUpdateTask({ companyId, contactId, taskId: editingTask.id, payload });
  };

  const handleAddTaskClick = () => {
    if (isPreviewing) return;
    if (!taskFormRef.current?.isValid) return;
    taskFormRef.current.submit();
  };

  const previewTask = (task) => {
    setEditingTask(task);
    setIsEditMode(false);
  };

  const startEditingTask = (task) => {
    setEditingTask(task);
    setIsEditMode(true);
  };

  const cancelEditingTask = () => {
    setEditingTask(null);
    setIsEditMode(false);
  };

  const columns = [
    {
      id: 'name',
      label: 'Name',
      defaultWidth: 160,
      minWidth: 120,
      cell: (task) => (
        <span className="truncate block text-sm" title={getAssigneeName(task)}>
          {getAssigneeName(task)}
        </span>
      ),
    },
    {
      id: 'title',
      label: 'Title',
      defaultWidth: 220,
      minWidth: 140,
      cell: (task) => (
        <span className="truncate block text-sm" title={task.title || ''}>
          {task.title}
        </span>
      ),
    },
    {
      id: 'due',
      label: 'Due Date',
      defaultWidth: 170,
      minWidth: 140,
      cell: (task) => (
        <span className="truncate block text-sm">
          {formatDate(task.due_at)}
        </span>
      ),
    },
    {
      id: 'completed',
      label: 'Completed Date',
      defaultWidth: 170,
      minWidth: 140,
      cell: (task) => (
        <span className="truncate block text-sm">
          {formatDate(task.completed_at)}
        </span>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      defaultWidth: 130,
      minWidth: 110,
      cell: (task) => (
        <TableChip tone={task.completed_at ? 'success' : 'warning'}>
          {task.completed_at ? 'Completed' : 'Pending'}
        </TableChip>
      ),
    },
    {
      id: 'actions',
      label: 'Action',
      defaultWidth: 140,
      minWidth: 120,
      align: 'center',
      stopPropagation: true,
      cell: (task) => (
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={() => {
              if (!readOnly && canUpdate && !task.completed_at) {
                handleCompleteTask({ companyId, contactId, taskId: task.id });
              }
            }}
            disabled={
              isCompleting || readOnly || !canUpdate || !!task.completed_at
            }
            className={`w-5 h-5 rounded-md flex items-center justify-center border transition-all focus:outline-none shrink-0 ${
              task.completed_at
                ? 'bg-[#50AB3D] border-[#50AB3D] text-white'
                : 'bg-white border-[#868686] text-transparent hover:border-[#50AB3D]'
            }`}
            title={task.completed_at ? 'Task completed' : 'Mark as complete'}
          >
            <svg
              className="w-3 h-3"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="3"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </button>
          {!readOnly && canUpdate && (
            <button
              type="button"
              onClick={() => startEditingTask(task)}
              disabled={!!task.completed_at}
              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] transition-colors focus:outline-none disabled:opacity-40"
              title={
                task.completed_at
                  ? 'Completed tasks cannot be edited'
                  : 'Edit Task'
              }
            >
              <img src={editIcon} alt="" className="w-4 h-4 object-contain" />
            </button>
          )}
          {!readOnly && canDelete && (
            <button
              type="button"
              onClick={() => setTaskToDelete(task.id)}
              className="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg hover:bg-[#FEEFEF] transition-colors focus:outline-none"
              title="Delete Task"
            >
              <img src={trashIcon} alt="" className="w-4 h-4 object-contain" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-3 overflow-hidden">
      <div className="shrink-0 rounded-[16px] bg-[#F5F5F5] p-4 sm:p-5">
        {!readOnly && (isEditMode ? canUpdate : canCreate) && (
          <CRMTaskForm
            ref={taskFormRef}
            variant="inline"
            initialData={editingTask}
            readOnly={isPreviewing}
            onSubmit={isEditMode ? submitEditTask : submitAddTask}
            isPending={isEditMode ? isUpdating : isAdding}
            companyId={companyId}
          />
        )}
      </div>

      {/* Toolbar */}
      <div className="flex w-full shrink-0 flex-wrap items-center gap-3">
        <div className="order-2 sm:order-1 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 flex-1 min-w-0 w-full sm:w-auto">
          <SearchInput
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search by title or assignee"
            className="w-full sm:max-w-sm"
            inputClassName="h-[44px]"
          />

          <div className="grid grid-cols-3 sm:flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto shrink-0 py-0.5">
            {STAT_FILTERS.map((filter) => {
              const isActive = statFilter === filter.id;
              return (
                <button
                  key={filter.id}
                  type="button"
                  onClick={() => {
                    setStatFilter(filter.id);
                    setPage(1);
                  }}
                  className="inline-flex items-center justify-center gap-1 sm:gap-2 h-[36px] sm:h-[38px] px-1.5 sm:px-3.5 rounded-[10px] text-[11px] min-[375px]:text-xs sm:text-sm font-medium text-[#1F2241] border transition-colors focus:outline-none min-w-0 whitespace-nowrap"
                  style={
                    isActive
                      ? {
                          backgroundColor: `${filter.dot}1A`,
                          borderColor: filter.dot,
                        }
                      : {
                          backgroundColor: '#F5F5F5',
                          borderColor: 'transparent',
                        }
                  }
                >
                  <span
                    className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full shrink-0"
                    style={{ backgroundColor: filter.dot }}
                  />
                  <span className="truncate">{filter.label}</span>
                  <span className="font-semibold shrink-0">
                    {String(stats[filter.id] ?? 0).padStart(2, '0')}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {isEditMode ? (
          <div className="order-1 sm:order-2 flex items-center gap-2 w-full sm:w-auto shrink-0 sm:ml-auto">
            <Button
              variant="secondary"
              onClick={cancelEditingTask}
              disabled={isUpdating}
              className="h-[44px] px-4 flex-1 sm:flex-none"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddTaskClick}
              disabled={isUpdating}
              className="h-[44px] px-4 flex-1 sm:flex-none"
            >
              <span className="whitespace-nowrap">
                {isUpdating ? 'Saving...' : 'Save Change'}
              </span>
              <span aria-hidden="true">→</span>
            </Button>
          </div>
        ) : (
          !readOnly &&
          canCreate && (
            <Button
              variant="primary"
              onClick={handleAddTaskClick}
              disabled={isAdding || isPreviewing}
              className="order-1 sm:order-2 h-[44px] px-4 w-full sm:w-auto shrink-0 sm:ml-auto"
            >
              <span className="whitespace-nowrap">
                {isAdding ? 'Adding...' : 'Add Task'}
              </span>
              <span aria-hidden="true">→</span>
            </Button>
          )
        )}
      </div>

      <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
        <V2Table
          tight
          columns={columns}
          data={isError && !tasksData ? [] : pagedTasks}
          rowKey="id"
          storageKey="crm_contact_tasks_column_widths"
          loading={isLoading || (!tasksData && !isError)}
          loadingRowCount={PAGE_SIZE}
          notice={
            isError && !tasksData
              ? parseApiError(error)?.message || 'Failed to load tasks'
              : null
          }
          empty="No tasks found."
          onRowClick={previewTask}
          isRowSelected={(task) => editingTask?.id === task.id}
          pagination={{
            currentPage: page,
            totalCount,
            itemsPerPage: PAGE_SIZE,
            onChange: setPage,
          }}
        />
      </div>

      <ConfirmDeleteModal
        isOpen={!!taskToDelete}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
        onCancel={() => setTaskToDelete(null)}
        onConfirm={() =>
          handleDeleteTask({ companyId, contactId, taskId: taskToDelete })
        }
      />
    </div>
  );
}
